window.ttx = window.ttx || {};
// 注意：ttx.config 可能已被其他模块（如 tx.rest.js 中 ttx.config.getDefineStyle）占用，
// 这里使用 || 保留已有属性，仅追加 getDomainCode / getApplicationCode
ttx.config = ttx.config || {};

// ============================================================
// 配置中心：define / get / set / getAll
// ============================================================

/** 已声明的配置项：key -> { default, validate, desc } */
ttx.config._schema = ttx.config._schema || {};
/** 当前配置值：key -> value（仅存放被 set 过的项，未 set 的读 default） */
ttx.config._values = ttx.config._values || {};
/** 变更监听器 */
ttx.config._listeners = ttx.config._listeners || [];

/**
 * 声明配置项（幂等，重复声明会覆盖默认值与校验）
 * @param {Object} schema - { key: { default, validate, desc } }，value 也可直接写默认值
 * @returns {Object} ttx.config
 */
ttx.config.define = function (schema) {
    if (!schema || typeof schema !== 'object') return this;
    Object.keys(schema).forEach(function (key) {
        var def = schema[key];
        var item = (def && typeof def === 'object' && !Array.isArray(def))
            ? { default: def.default, validate: def.validate, desc: def.desc }
            : { default: def };
        this._schema[key] = item;
    }, this);
    return this;
};

/** 是否已声明该配置项 */
ttx.config.has = function (key) {
    return Object.prototype.hasOwnProperty.call(this._schema, key);
};

/**
 * 读取配置：优先取 set 过的值，其次取声明的默认值，最后取 fallback
 */
ttx.config.get = function (key, fallback) {
    if (Object.prototype.hasOwnProperty.call(this._values, key)) return this._values[key];
    var item = this._schema[key];
    if (item && item.default !== undefined) return item.default;
    return fallback;
};

/**
 * 写入配置：声明了 validate 且校验不通过时不写入
 * @returns {boolean} 是否写入成功
 */
ttx.config.set = function (key, value) {
    var item = this._schema[key];
    if (item && typeof item.validate === 'function' && item.validate(value) === false) {
        ttx.console.warn('[ttx.config.set] 校验失败，已忽略: ' + key);
        return false;
    }
    var prev = this.get(key);
    this._values[key] = value;
    this._notify(key, value, prev);
    return true;
};

/** 全量读取：声明的默认值与已 set 的值合并 */
ttx.config.getAll = function () {
    var all = {};
    Object.keys(this._schema).forEach(function (key) {
        if (this._schema[key].default !== undefined) all[key] = this._schema[key].default;
    }, this);
    Object.keys(this._values).forEach(function (key) {
        all[key] = this._values[key];
    }, this);
    return all;
};

/** 重置：不传 key 则重置全部 */
ttx.config.reset = function (key) {
    if (key === undefined) {
        this._values = {};
        return this;
    }
    delete this._values[key];
    return this;
};

/**
 * 订阅配置变更
 * @param {Function} fn - (key, value, prev) => void
 * @returns {Function} 注销函数
 */
ttx.config.onChange = function (fn) {
    if (typeof fn !== 'function') return function () { };
    this._listeners.push(fn);
    return function () {
        var idx = ttx.config._listeners.indexOf(fn);
        if (idx >= 0) ttx.config._listeners.splice(idx, 1);
    };
};

ttx.config._notify = function (key, value, prev) {
    this._listeners.slice().forEach(function (fn) {
        try {
            fn(key, value, prev);
        } catch (e) {
            ttx.console.error('[ttx.config] onChange 回调异常:', e);
        }
    });
};

// ============================================================
// 特性开关：ttx.feature（全局 + 作用域）
//   scope 省略 = 全局；scope 为字符串 = 域(domainKey)；scope 为对象 = 沿用其 features 属性（如 model 实例）
// ============================================================
ttx.feature = ttx.feature || {};

ttx.feature._global = ttx.feature._global || { whitelist: new Set(), blacklist: new Set() };
ttx.feature._domains = ttx.feature._domains || new Map();

/** 只读查找作用域开关容器，不存在返回 null（不产生副作用） */
ttx.feature._read = function (scope) {
    if (scope === undefined || scope === null) return this._global;
    if (typeof scope === 'string') return this._domains.get(scope) || null;
    if (typeof scope === 'object') return scope.features || null;
    return this._global;
};

/** 读写查找作用域开关容器，不存在则创建 */
ttx.feature._store = function (scope) {
    if (scope === undefined || scope === null) return this._global;
    if (typeof scope === 'string') {
        if (!this._domains.has(scope)) this._domains.set(scope, { whitelist: new Set(), blacklist: new Set() });
        return this._domains.get(scope);
    }
    if (typeof scope === 'object') {
        if (!scope.features) scope.features = { whitelist: new Set(), blacklist: new Set() };
        if (!scope.features.whitelist) scope.features.whitelist = new Set();
        if (!scope.features.blacklist) scope.features.blacklist = new Set();
        return scope.features;
    }
    return this._global;
};

/**
 * 判断特性是否开启：作用域黑名单 > 作用域白名单 > 全局黑名单 > 全局白名单 > false
 * @param {string} name - 特性名
 * @param {string|Object} [scope] - 域编码 / 对象作用域（如 model 实例）
 */
ttx.feature.isEnabled = function (name, scope) {
    var local = this._read(scope);
    if (local) {
        if (local.blacklist.has(name)) return false;
        if (local.whitelist.has(name)) return true;
    }
    if (local !== this._global) {
        if (this._global.blacklist.has(name)) return false;
        return this._global.whitelist.has(name);
    }
    return false;
};

/** 开启特性 */
ttx.feature.enable = function (name, scope) {
    var store = this._store(scope);
    store.whitelist.add(name);
    store.blacklist.delete(name);
    return this;
};

/** 关闭特性 */
ttx.feature.disable = function (name, scope) {
    var store = this._store(scope);
    store.whitelist.delete(name);
    store.blacklist.add(name);
    return this;
};

/** 批量开启 */
ttx.feature.whitelist = function (names, scope) {
    (names || []).forEach(function (name) { ttx.feature.enable(name, scope); });
    return this;
};

/** 批量关闭 */
ttx.feature.blacklist = function (names, scope) {
    (names || []).forEach(function (name) { ttx.feature.disable(name, scope); });
    return this;
};

/** 读取某作用域的全部开关 */
ttx.feature.getAll = function (scope) {
    var store = this._read(scope) || { whitelist: new Set(), blacklist: new Set() };
    return { whitelist: Array.from(store.whitelist), blacklist: Array.from(store.blacklist) };
};

/** 重置：不传 scope 则重置全局与所有域 */
ttx.feature.reset = function (scope) {
    if (scope === undefined) {
        this._global = { whitelist: new Set(), blacklist: new Set() };
        this._domains = new Map();
        return this;
    }
    if (typeof scope === 'string') {
        this._domains.delete(scope);
    } else if (scope && typeof scope === 'object') {
        scope.features = { whitelist: new Set(), blacklist: new Set() };
    }
    return this;
};

/**
 * 按域批量设置特性（域配置来自 loadExtend 的 domainConfigs.features）
 * @param {string} domainKey
 * @param {Object|Array} features - { name: true } 映射，或特性名数组
 */
ttx.feature.setDomain = function (domainKey, features) {
    if (!domainKey || !features) return this;
    if (Array.isArray(features)) return this.whitelist(features, domainKey);
    Object.keys(features).forEach(function (name) {
        features[name] ? ttx.feature.enable(name, domainKey) : ttx.feature.disable(name, domainKey);
    });
    return this;
};

// ============================================================
// MDF cb.config API 补齐
// ============================================================

/**
 * 获取工作台领域编码
 * 对应 MDF cb.config.getDomainCode
 * @param {object} viewmodel 必填，页面级 ViewModel
 * @returns {string} 领域编码
 */
ttx.config.getDomainCode = function (viewmodel) {
    if (!viewmodel) {
        ttx.console.warn('[ttx.config.getDomainCode] viewmodel 必填');
        return '';
    }
    // 优先从 viewmodel.getDomainKey() 取
    if (typeof viewmodel.getDomainKey === 'function') {
        var dk = viewmodel.getDomainKey();
        if (dk) return dk;
    }
    // 其次从 ttx.context 取
    try {
        if (ttx.context && typeof ttx.context.getDomainKey === 'function') {
            var ctxDk = ttx.context.getDomainKey();
            if (ctxDk) return ctxDk;
        }
    } catch (_) { }
    // 最后从 window.jDiwork 取
    try {
        if (window.jDiwork && typeof window.jDiwork.diworkContext === 'function') {
            var ctx = window.jDiwork.diworkContext() || {};
            if (ctx.domainKey) return ctx.domainKey;
        }
    } catch (_) { }
    return '';
};

/**
 * 获取工作台应用编码
 * 对应 MDF cb.config.getApplicationCode
 * @param {object} viewmodel 必填，页面级 ViewModel
 * @returns {string} 应用编码
 */
ttx.config.getApplicationCode = function (viewmodel) {
    if (!viewmodel) {
        ttx.console.warn('[ttx.config.getApplicationCode] viewmodel 必填');
        return '';
    }
    // 优先从 window.jDiwork.diworkContext().applicationCode 取
    try {
        if (window.jDiwork && typeof window.jDiwork.diworkContext === 'function') {
            var ctx = window.jDiwork.diworkContext() || {};
            if (ctx.applicationCode) return ctx.applicationCode;
        }
    } catch (_) { }
    // 其次从 ttx.context 取
    try {
        if (ttx.context && typeof ttx.context.getApplicationCode === 'function') {
            var appCode = ttx.context.getApplicationCode();
            if (appCode) return appCode;
        }
    } catch (_) { }
    // 兜底：从 viewmodel 的 params/query 取
    try {
        if (typeof viewmodel.getParams === 'function') {
            var params = viewmodel.getParams() || {};
            if (params.applicationCode) return params.applicationCode;
            if (params.query && params.query.applicationCode) return params.query.applicationCode;
            if (params.appCode) return params.appCode;
        }
    } catch (_) { }
    return '';
};
